import React, { useRef, useState, useEffect } from "react";
import { Typography, Button, Paper, Tooltip } from "@material-ui/core";
import { useFormik } from "formik";
import { useTranslation } from "@/langs/useTranslation";
import { BreadCrumbs } from "@/components/device/BreadCrumbs";
import useStyles from "@/styles/groupsStyle";
import Capacity from "@/components/fileBrowser/Capacity";
import SearchInput from "@/components/SearchInput";
import SingleSelector from "@/components/SingleSelector";
import { FormItem } from "@/components/Form";
import DateRangePicker from "@/components/DateRangePicker";
import UploadCMSResourceDialog from "../UploadCMSResourceDialog";
import { CMSResourceDatatable } from "../CMSResourceDatatable";
import * as dayjs from "dayjs";
import modalConfirm from "@/components/Modal/modalConfirm";
import { deleteCMSResource } from "@/apis/apiCollection";
import { useGlobalState } from "@/GlobalProvider";
import HasPermission from "@/components/commons/HasPermission";
import { HelpOutline } from "@material-ui/icons";

const initialValues = {
  mimeType: "",
  status: 0,
  date: [null, null],
  keywords: ""
};

const Index = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [uploadOpen, setUploadOpen] = useState(false);
  const tableRef = useRef(null);
  const { currentCompanyId } = useGlobalState();
  const capRef = useRef(null);
  const [cap, setCap] = useState(0);

  const { values, handleChange, handleSubmit, handleReset } = useFormik({
    initialValues,
    onSubmit: () => {
      handleReloadDataTable();
    }
  });

  const handleReloadDataTable = () => {
    // reload
    if (tableRef.current) tableRef.current.reload();
    if (capRef.current) capRef.current.refresh();
  };

  const resetQuery = () => {
    handleReset();
    handleReloadDataTable();
  };

  const getSearchParams = () => {
    let statuses = [];
    if (values.status == 1) statuses = [1, 2];
    else if (values.status == 0) statuses = [];
    else statuses = [values.status];
    // const formValues = isReset.current ? initialValues : values;
    return {
      mimeType: [values.mimeType].filter(v => v),
      createdAtStart: values.date[0] ? dayjs(values.date[0]).valueOf() : 0,
      createdAtEnd: values.date[1] ? dayjs(values.date[1]).valueOf() : 0,
      keywords: values.keywords,
      statuses
    };
  };

  const handleDelete = row => {
    modalConfirm({
      title: t({ defaultMessage: "删除提醒" }),
      content: t(
        {
          defaultMessage: "是否确认删除（{name}）该资源？"
        },
        { name: row.name }
      ),
      onConfirm: async () => {
        await deleteCMSResource({ id: row.id });
        handleReloadDataTable();
      }
    });
  };

  useEffect(() => {
    resetQuery();
  }, [currentCompanyId]);

  return (
    <>
      <BreadCrumbs
        noTranslate
        pageName={t({ defaultMessage: "资源管理中心" })}
      />
      <div className={classes.titleWithSelect}>
        <Typography variant="h4">
          {t({ defaultMessage: "资源管理中心" })}
        </Typography>
        <Tooltip
          title={t({
            defaultMessage:
              "本平台上傳的數位內容將經由 Amazon Rekognition內容審核 自動審查，以降低公開發佈的風險。若自動審查未通過，您可選擇手動審核通過；但在此情況下，本平台將不對任何後續影響承擔責任。"
          })}
        >
          <HelpOutline />
        </Tooltip>
      </div>
      <div style={{ display: "flex", height: 40 }}>
        <Capacity ref={capRef} onCapRefresh={setCap} />
        <SearchInput
          placeholder={t({ defaultMessage: "搜索" })}
          name="keywords"
          icon={null}
          value={values.keywords}
          onChange={handleChange}
          style={{ marginLeft: "auto" }}
        />
        <HasPermission permission="resource_library_upload_content_resource">
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              // upload dialog
              setUploadOpen(true);
            }}
            style={{ marginLeft: 16, height: 35 }}
          >
            {t({ defaultMessage: "上传素材" })}
          </Button>
        </HasPermission>
      </div>
      <Paper
        elevation={0}
        style={{
          marginTop: 32,
          display: "flex",
          flexDirection: "column",
          flex: 1
        }}
      >
        <form onSubmit={handleSubmit}>
          <div className={classes.tableQueryForm}>
            <FormItem
              label={t({ defaultMessage: "类型" })}
              className={classes.formItem}
            >
              <SingleSelector
                // placeholder={t({ defaultMessage: "选择" })}
                clearable={false}
                options={[
                  { id: "", name: t({ defaultMessage: "全部" }) },
                  { id: "image/png", name: "PNG" },
                  { id: "image/jpeg", name: "JPEG" },
                  { id: "video/mp4", name: "MPEG-4" },
                  { id: "video/mov", name: "MOV" }
                ]}
                name="mimeType"
                value={values.mimeType}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem
              label={t({ defaultMessage: "状态" })}
              className={classes.formItem}
            >
              <SingleSelector
                // placeholder={t({ defaultMessage: "选择状态" })}
                clearable={false}
                options={[
                  { id: 0, name: t({ defaultMessage: "全部" }) },
                  { id: 3, name: t({ defaultMessage: "通过" }) },
                  { id: 1, name: t({ defaultMessage: "待审核" }) },
                  { id: 4, name: t({ defaultMessage: "失败" }) }
                ]}
                name="status"
                value={values.status}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem
              label={t({ defaultMessage: "上传时间" })}
              className={classes.formItem}
            >
              <DateRangePicker
                name="date"
                value={values.date}
                onChange={handleChange}
              />
            </FormItem>
            <FormItem className={classes.formItem}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                style={{ marginRight: 8 }}
              >
                {t({ defaultMessage: "查询" })}
              </Button>
              <Button variant="outlined" onClick={resetQuery}>
                {t({ defaultMessage: "重置" })}
              </Button>
            </FormItem>
          </div>
        </form>
        <CMSResourceDatatable
          ref={tableRef}
          getSearchParams={getSearchParams}
          onDelete={handleDelete}
        />
      </Paper>
      <UploadCMSResourceDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSuccess={() => handleReloadDataTable()}
        cap={cap}
      />
    </>
  );
};

export default Index;
